// The saved-chat rail — the caller's own past conversations, newest activity first.
//
// The list comes from GET /chat-events/history, which reads the transcript the broker consumer
// persisted at end-of-turn. Two consequences the UI has to be honest about:
//
//  1. A CONVERSATION APPEARS ONLY AFTER A TURN COMPLETES. The engine publishes on the success path
//     only, and delivery is asynchronous (topic → durable subscription → HTTP → Mongo), so the chat
//     in progress is normally absent from this list until the turn lands. The live conversation is
//     therefore shown from local state, not from the fetch, and never waits for it.
//  2. TITLES ARE DERIVED, not stored — the server clips the opening question. A conversation whose
//     first message never reached the store reads as "Untitled chat".
//
// THE "MINE ONLY" BOX (`mineBox`, RSM4 — design v2 D21; always on the internal page, whatever the resume switch says): at the top,
// ticked by default (the caller's own chats, as before); unticked, every internal user's chats, each row saying whose it is
// ("<owner> — 5 Oct"). CONTINUING CHATS ON (the server says so: `resumeOn`, design v2 D4): no row shows a count of its messages — no
// count of questions asked is shown anywhere (the developer, 5 Oct). Off, the rows are as before.
//
// Search filters WHAT HAS BEEN FETCHED; it is not a server query. The listing is paged, so this
// narrows the pages loaded so far rather than searching the archive — which is why an empty result
// says so rather than "no matches", and why the Load more button stays visible under one: a chat
// the reader is looking for may simply be on a page nobody has asked for yet.

import React, { useMemo, useState } from "react";
import { useNearEdge } from "../useNearEdge";
import { formatRailTime } from "../messageTime.mjs";
import { MessageSquarePlus, Search } from "lucide-react";
import styles from "../sqAi.module.css";

// The Load more button belongs to the LIST, so it is hidden while the first page is still loading
// and while the list is in an error state — in both cases there is no established end to extend.
function listLoadingGuard(loading, error) {
  return Boolean(loading || error);
}

// Whose a chat is, in a row of every internal user's list: the name its owner last asked under, else who it is to the reader.
export function ownerLabel(owner) {
  if (!owner) return "";
  if (typeof owner.name === "string" && owner.name.trim()) return owner.name.trim();
  return owner.self ? "You" : "Another user";
}

export default function HistoryRail({
  conversations,
  loading,
  error,
  // The conversation the rail is currently SHOWING, live or saved. Drives the selected row.
  activeId,
  // The live conversation's id, so the row for it can be labelled and cannot be re-opened
  // read-only while it is the one on screen.
  liveConversationId,
  liveHasMessages,
  onOpen,
  onNewChat,
  newChatDisabled,
  // Paging. `hasMore` is the server's own answer (a non-null nextCursor), never inferred from the
  // page being short.
  hasMore,
  loadingMore,
  onLoadMore,
  // A page that failed to load: the rows already shown stay, and a button asks again (the scroll no longer does, or a dead
  // connection would be asked in a loop).
  loadMoreFailed = false,
  // The reader's zone — the page's own timezone setting — for the time on each row. Coarse on purpose: the rail answers "which
  // chat", not "exactly when" — the clock on today's rows, the date on every other day's, in sq-app's forms.
  zone,
  // Continuing chats (RSM4): whether the server has it on. The "Mine only" box is the internal page's own, switch or none.
  resumeOn = false,
  mineBox = false,
  mineOnly = true,
  onMineOnlyChange,
}) {
  const [query, setQuery] = useState("");

  // Loaded by SCROLLING, not by a button: the next page is what the list continues into, so
  // reaching the end IS the request. Disabled while a page is in flight so one scroll cannot
  // queue several.
  const { sentinelRef } = useNearEdge({
    enabled: Boolean(hasMore) && !loadingMore && !loadMoreFailed,
    onReach: onLoadMore,
    // The rail appends downward, so the bottom is the edge that asks for more.
    edge: "bottom",
  });

  const rows = useMemo(() => {
    // The live conversation is prepended from local state when the fetch has not caught up with it
    // yet, so sending the first message makes a row appear immediately instead of after the round
    // trip. Matched on id, so once the fetch does return it there is one row, not two.
    const fetched = Array.isArray(conversations) ? conversations : [];
    const haveLive = fetched.some((c) => c.conversationId === liveConversationId);
    const list =
      liveHasMessages && !haveLive
        ? [{ conversationId: liveConversationId, title: "Current chat", messages: 0, lastAt: null }, ...fetched]
        : fetched;

    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => (c.title || "").toLowerCase().includes(q));
  }, [conversations, query, liveConversationId, liveHasMessages]);

  return (
    <aside className={styles.rail} aria-label="Saved chats">
      <button type="button" className={styles.railNew} onClick={onNewChat} disabled={newChatDisabled}>
        <MessageSquarePlus className="w-3.5 h-3.5" strokeWidth={2} />
        <span>New chat</span>
      </button>

      {mineBox && (
        <label className={styles.railMine}>
          <input type="checkbox" checked={mineOnly} onChange={(e) => onMineOnlyChange && onMineOnlyChange(e.target.checked)} />
          <span>Mine only</span>
        </label>
      )}

      <div className={styles.railSearch}>
        <Search className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chats"
          aria-label="Search saved chats"
        />
      </div>

      <div className={styles.railList}>
        {loading && <p className={styles.railNote}>Loading chats…</p>}

        {/* A failed fetch says so instead of rendering an empty list — an empty rail and an
            unreachable store look identical, and only one of them means "you have no chats". */}
        {!loading && error && <p className={styles.railNote}>{error}</p>}

        {!loading && !error && rows.length === 0 && (
          <p className={styles.railNote}>
            {query.trim() ? "No matches in the chats loaded so far." : "No saved chats yet."}
          </p>
        )}

        {rows.map((c) => {
          const isLive = c.conversationId === liveConversationId;
          return (
            <button
              key={c.conversationId}
              type="button"
              className={`${styles.railItem} ${c.conversationId === activeId ? styles.railItemActive : ""}`}
              onClick={() => onOpen(c.conversationId, c.title)}
              title={c.title}
            >
              <span className={styles.railItemTitle}>{c.title}</span>
              <span className={styles.railItemMeta}>
                {mineBox && !mineOnly && c.owner ? `${ownerLabel(c.owner)} — ` : ""}
                {isLive ? "live" : formatRailTime(c.lastAt, { zone })}
                {!resumeOn && !isLive && c.messages ? ` · ${c.messages}` : ""}
              </span>
            </button>
          );
        })}

        {/* The sentinel sits BELOW the rows: crossing it is the request for the next page. Kept
            mounted under an empty search result too, because the match may be on a page nobody has
            fetched yet — a filtered-to-nothing list must still be able to keep loading. */}
        {!listLoadingGuard(loading, error) && hasMore && !loadMoreFailed && (
          <div ref={sentinelRef} className={styles.railSentinel} aria-hidden="true">
            {loadingMore && <span className={styles.spinner} role="status" aria-label="Loading more chats" />}
          </div>
        )}
        {!listLoadingGuard(loading, error) && hasMore && loadMoreFailed && (
          <div className={styles.railSentinel}>
            <button type="button" className={styles.ghostBtn} onClick={onLoadMore}>
              Load more
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
